import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  ArrowLeft, 
  Shield, 
  CheckCircle2,
  Clock,
  XCircle,
  ChevronRight,
  Upload,
  User,
  MapPin,
  CreditCard,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import NavBar from "../components/NavBar";

const KYC_LEVELS = {
  0: { name: "Unverified", color: "text-gray-400", bg: "bg-gray-500/20" },
  1: { name: "Basic", color: "text-blue-400", bg: "bg-blue-500/20" },
  2: { name: "Enhanced", color: "text-purple-400", bg: "bg-purple-500/20" },
  3: { name: "Full", color: "text-emerald-400", bg: "bg-emerald-500/20" },
};

const KYC = ({ user }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState("status"); // status, form, success
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    date_of_birth: "",
    address_line1: "",
    city: "",
    country: "JM",
    postal_code: "",
    id_type: "passport",
    id_number: "",
    id_expiry: "",
  });

  // Fetch KYC status
  const { data: kycStatus, isLoading } = useQuery({
    queryKey: ["kycStatus"],
    queryFn: async () => {
      const response = await axios.get(`${API}/kyc/status`);
      return response.data;
    }
  });

  // Submit KYC
  const submitMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API}/kyc/submit`, formData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["kycStatus"]);
      setStep("success");
      toast.success("KYC Verified!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "KYC submission failed");
    }
  });

  // Upgrade KYC level
  const upgradeMutation = useMutation({
    mutationFn: async (level) => {
      const response = await axios.post(`${API}/kyc/upgrade-level?target_level=${level}`);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["kycStatus"]);
      toast.success(`Upgraded to ${data.level_name}!`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Upgrade failed");
    }
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const formatCurrency = (amount) => `$${amount.toLocaleString()}`;

  // Status View
  if (step === "status") {
    const levelInfo = KYC_LEVELS[kycStatus?.level || 0];
    const isVerified = kycStatus?.status === "verified";

    return (
      <div className="min-h-screen bg-background pb-24" data-testid="kyc-page">
        <header className="px-5 pt-4 pb-2 flex items-center gap-4 max-w-lg mx-auto">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/settings")}
            className="text-foreground"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading font-semibold text-xl">Identity Verification</h1>
            <p className="text-xs text-muted-foreground">KYC Status & Limits</p>
          </div>
        </header>

        <main className="px-5 max-w-lg mx-auto">
          {/* Status Card */}
          <div className="glass rounded-3xl p-6 mb-6">
            <div className="flex items-center gap-4 mb-4">
              <div className={`w-14 h-14 rounded-2xl ${levelInfo.bg} flex items-center justify-center`}>
                {isVerified ? (
                  <CheckCircle2 className={`w-7 h-7 ${levelInfo.color}`} />
                ) : (
                  <Clock className="w-7 h-7 text-yellow-400" />
                )}
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Verification Status</p>
                <p className={`text-xl font-semibold ${levelInfo.color}`}>
                  {levelInfo.name}
                </p>
              </div>
            </div>
            
            {!isVerified && (
              <Button
                className="w-full btn-primary h-12"
                onClick={() => setStep("form")}
                data-testid="start-kyc-btn"
              >
                <Shield className="w-4 h-4 mr-2" />
                Verify Identity
              </Button>
            )}
          </div>

          {/* Limits Card */}
          <div className="glass rounded-2xl p-5 mb-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
              Transaction Limits
            </p>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Single Transfer</span>
                <span className="text-sm font-mono text-foreground">
                  {formatCurrency(kycStatus?.limits?.single_transfer || 500)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Daily Limit</span>
                <span className="text-sm font-mono text-foreground">
                  {formatCurrency(kycStatus?.limits?.daily_transfer || 1000)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Monthly Limit</span>
                <span className="text-sm font-mono text-foreground">
                  {formatCurrency(kycStatus?.limits?.monthly_transfer || 5000)}
                </span>
              </div>
            </div>
          </div>

          {/* Upgrade Options */}
          {isVerified && kycStatus?.level < 3 && (
            <div className="mb-6">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
                Upgrade Verification
              </p>
              <div className="space-y-3">
                {[2, 3].filter(l => l > kycStatus?.level).map((level) => {
                  const info = KYC_LEVELS[level];
                  return (
                    <button
                      key={level}
                      onClick={() => upgradeMutation.mutate(level)}
                      disabled={upgradeMutation.isPending}
                      className="w-full glass rounded-2xl p-4 flex items-center justify-between
                        hover:bg-white/10 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl ${info.bg} flex items-center justify-center`}>
                          <Sparkles className={`w-5 h-5 ${info.color}`} />
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-foreground">{info.name} KYC</p>
                          <p className="text-xs text-muted-foreground">
                            Up to {formatCurrency(level === 2 ? 50000 : 1000000)}/day
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground text-center mt-3">
                Sandbox mode: Instant upgrades for testing
              </p>
            </div>
          )}

          {/* Info */}
          <div className="glass rounded-2xl p-4">
            <p className="text-xs text-muted-foreground text-center">
              <strong>Sandbox Mode:</strong> KYC verification is simulated for testing.
              In production, documents would be verified by our compliance team.
            </p>
          </div>
        </main>

        <NavBar active="profile" />
      </div>
    );
  }

  // Form View
  if (step === "form") {
    return (
      <div className="min-h-screen bg-background pb-8" data-testid="kyc-form">
        <header className="px-5 pt-4 pb-2 flex items-center gap-4 max-w-lg mx-auto">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setStep("status")}
            className="text-foreground"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading font-semibold text-xl">Verify Identity</h1>
        </header>

        <main className="px-5 max-w-lg mx-auto">
          <form onSubmit={(e) => { e.preventDefault(); submitMutation.mutate(); }} className="space-y-6">
            {/* Personal Info */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <User className="w-4 h-4 text-primary" />
                <p className="text-sm font-medium text-foreground">Personal Information</p>
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    name="first_name"
                    placeholder="First Name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className="input-glass"
                    required
                  />
                  <Input
                    name="last_name"
                    placeholder="Last Name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className="input-glass"
                    required
                  />
                </div>
                <Input
                  name="date_of_birth"
                  type="date"
                  placeholder="Date of Birth"
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                  className="input-glass"
                  required
                />
              </div>
            </div>

            {/* Address */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4 text-primary" />
                <p className="text-sm font-medium text-foreground">Address</p>
              </div>
              <div className="space-y-3">
                <Input
                  name="address_line1"
                  placeholder="Street Address"
                  value={formData.address_line1}
                  onChange={handleInputChange}
                  className="input-glass"
                  required
                />
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    name="city"
                    placeholder="City"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="input-glass"
                    required
                  />
                  <Input
                    name="postal_code"
                    placeholder="Postal Code"
                    value={formData.postal_code}
                    onChange={handleInputChange}
                    className="input-glass"
                    required
                  />
                </div>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="w-full input-glass rounded-xl"
                  required
                >
                  <option value="JM">Jamaica</option>
                  <option value="US">United States</option>
                  <option value="GB">United Kingdom</option>
                  <option value="CA">Canada</option>
                </select>
              </div>
            </div>

            {/* ID Document */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <CreditCard className="w-4 h-4 text-primary" />
                <p className="text-sm font-medium text-foreground">ID Document</p>
              </div>
              <div className="space-y-3">
                <select
                  name="id_type"
                  value={formData.id_type}
                  onChange={handleInputChange}
                  className="w-full input-glass rounded-xl"
                  required
                >
                  <option value="passport">Passport</option>
                  <option value="drivers_license">Driver's License</option>
                  <option value="national_id">National ID</option>
                </select>
                <Input
                  name="id_number"
                  placeholder="ID Number"
                  value={formData.id_number}
                  onChange={handleInputChange}
                  className="input-glass"
                  required
                />
                <Input
                  name="id_expiry"
                  type="date"
                  placeholder="Expiry Date"
                  value={formData.id_expiry}
                  onChange={handleInputChange}
                  className="input-glass"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full btn-primary h-14 text-base"
              disabled={submitMutation.isPending}
              data-testid="submit-kyc-btn"
            >
              {submitMutation.isPending ? "Verifying..." : "Submit for Verification"}
            </Button>
          </form>
        </main>
      </div>
    );
  }

  // Success View
  if (step === "success") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-400" />
          </div>
          <h2 className="font-heading font-bold text-2xl mb-2">Verified!</h2>
          <p className="text-muted-foreground mb-8">
            Your identity has been verified. You now have higher transaction limits.
          </p>
          <Button
            className="btn-primary h-12 px-8"
            onClick={() => {
              setStep("status");
              queryClient.invalidateQueries(["kycStatus"]);
            }}
          >
            Continue
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default KYC;
